import {
  LegalDocLayout,
  LP, LH3, LUl, LLi, LOl, LNote, LTable,
  type LegalSection,
} from './LegalDocLayout';

const sections: LegalSection[] = [
  {
    id: 'introduction',
    heading: 'Introduction',
    body: (
      <>
        <LP>
          Zykes Technologies LLP (<strong>"Zykes"</strong>, <strong>"we"</strong>,{' '}
          <strong>"us"</strong>, or <strong>"our"</strong>) operates the GoGrabJob Business
          platform and related services (collectively, the <strong>"Services"</strong>). We are
          committed to protecting the personal information of our customers, their users, job
          applicants, and everyone who interacts with our Services.
        </LP>
        <LP>
          This Privacy Policy explains what personal data we collect, why we collect it, how we
          use and share it, and the choices you have regarding your information. It applies to all
          visitors, registered users, and customers of GoGrabJob Business (
          <strong>gograbjob.com/business</strong> and associated subdomains).
        </LP>
        <LNote>
          If you are a job applicant whose data was submitted through a GoGrabJob Business customer
          (an employer or recruitment agency), please also review that organisation's own privacy
          notice. Zykes processes applicant data as a data processor on behalf of the employer
          acting as the data controller.
        </LNote>
        <LP>
          By accessing or using our Services, you acknowledge that you have read, understood, and
          agree to the practices described in this Privacy Policy. If you do not agree, please
          discontinue use of our Services.
        </LP>
      </>
    ),
  },
  {
    id: 'who-we-are',
    heading: 'Who We Are',
    body: (
      <>
        <LP>
          <strong>Data Controller (for customer and user data):</strong>
          <br />
          Zykes Technologies LLP
          <br />
          Registered in India under the Limited Liability Partnership Act, 2008
          <br />
          Registered Office: India
        </LP>
        <LP>
          <strong>Data Processor (for candidate / applicant data):</strong>
          <br />
          When our customers (employers, recruitment agencies) use GoGrabJob Business to manage
          hiring, Zykes processes candidate personal data solely under the documented instructions
          of the customer, who acts as the data controller for that data.
        </LP>
        <LP>
          Our Data Protection contact:{' '}
          <a
            href="mailto:privacy@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            privacy@gograbjob.com
          </a>
        </LP>
      </>
    ),
  },
  {
    id: 'information-we-collect',
    heading: 'Information We Collect',
    body: (
      <>
        <LP>
          We collect personal data in three primary ways: information you provide directly,
          information collected automatically, and information obtained from third parties.
        </LP>

        <LH3>2.1 Information You Provide Directly</LH3>
        <LUl>
          <LLi>
            <strong>Account registration:</strong> Name, work email address, company name, role /
            job title, phone number, and password.
          </LLi>
          <LLi>
            <strong>Subscription & billing:</strong> Billing address, VAT / GST number, and
            payment information (processed by our payment processor; we do not store full card
            numbers).
          </LLi>
          <LLi>
            <strong>Profile & preferences:</strong> Profile photo, timezone, notification settings,
            and communication preferences.
          </LLi>
          <LLi>
            <strong>Support communications:</strong> Content of support tickets, chat messages, and
            feedback submissions.
          </LLi>
          <LLi>
            <strong>Customer-uploaded content:</strong> Job descriptions, candidate résumés, interview
            notes, scorecard responses, and other hiring data you or your team enters into the
            platform.
          </LLi>
        </LUl>

        <LH3>2.2 Information Collected Automatically</LH3>
        <LUl>
          <LLi>
            <strong>Usage telemetry:</strong> Pages and features accessed, actions taken, search
            queries, timestamps, and session duration.
          </LLi>
          <LLi>
            <strong>Device & connection data:</strong> IP address, browser type and version,
            operating system, device type, screen resolution, and referring URL.
          </LLi>
          <LLi>
            <strong>Cookies & similar technologies:</strong> Session identifiers, authentication
            tokens, and analytics cookies as described in our Cookie Policy.
          </LLi>
          <LLi>
            <strong>Log data:</strong> Server access logs, error logs, and API request records
            retained for security and debugging.
          </LLi>
        </LUl>

        <LH3>2.3 Information from Third Parties</LH3>
        <LUl>
          <LLi>
            <strong>Identity providers (SSO):</strong> When you authenticate using a third-party
            identity provider (e.g., Google Workspace, Microsoft Entra ID), we receive your name,
            email address, and authentication token.
          </LLi>
          <LLi>
            <strong>Integration partners:</strong> If you connect Gmail, Outlook, LinkedIn, or HRIS
            systems, we receive data from those services only to the extent authorised by you and
            necessary to provide the integration functionality.
          </LLi>
          <LLi>
            <strong>Job boards:</strong> Candidate profile data from job boards (e.g., Naukri,
            LinkedIn, Indeed) where you have enabled import, subject to each platform's own terms.
          </LLi>
        </LUl>
      </>
    ),
  },
  {
    id: 'how-we-use',
    heading: 'How We Use Your Information',
    body: (
      <>
        <LP>We use the personal data we collect for the following purposes:</LP>
        <LTable
          headers={['Purpose', 'Examples', 'Legal Basis (GDPR)']}
          rows={[
            [
              'Providing the Services',
              'Creating accounts, processing hiring pipelines, delivering AI interviews, generating role-fit scores',
              'Contract performance',
            ],
            [
              'Billing & payments',
              'Processing subscriptions, issuing invoices, managing upgrades and cancellations',
              'Contract performance',
            ],
            [
              'Customer support',
              'Responding to tickets, troubleshooting, remote assistance sessions',
              'Contract performance / Legitimate interest',
            ],
            [
              'Security & fraud prevention',
              'Detecting unauthorised access, bot mitigation, abuse prevention',
              'Legitimate interest',
            ],
            [
              'Product improvement',
              'Usage analytics, performance monitoring, A/B testing new features',
              'Legitimate interest',
            ],
            [
              'Marketing communications',
              'Product announcements, webinars, feature releases (opt-out available)',
              'Consent / Legitimate interest',
            ],
            [
              'Legal compliance',
              'Fulfilling regulatory obligations under Indian law, GDPR, and other applicable frameworks',
              'Legal obligation',
            ],
            [
              'Aggregated research',
              'Publishing anonymised industry benchmarks and hiring statistics',
              'Legitimate interest',
            ],
          ]}
        />
        <LP>
          We do not sell, rent, or trade your personal data to third parties for their own
          marketing purposes.
        </LP>
      </>
    ),
  },
  {
    id: 'legal-basis',
    heading: 'Legal Basis for Processing (GDPR / UK GDPR)',
    body: (
      <>
        <LP>
          For individuals in the European Economic Area (EEA) or the United Kingdom, we rely on
          the following legal bases under the General Data Protection Regulation:
        </LP>
        <LUl>
          <LLi>
            <strong>Performance of a contract (Art. 6(1)(b)):</strong> Processing necessary to
            deliver the Services you have subscribed to, including account management and
            transactional emails.
          </LLi>
          <LLi>
            <strong>Legitimate interests (Art. 6(1)(f)):</strong> Security monitoring, product
            analytics, preventing abuse, and B2B marketing communications to existing customers.
            We balance these interests against your rights and freedoms.
          </LLi>
          <LLi>
            <strong>Legal obligation (Art. 6(1)(c)):</strong> Retaining financial records,
            responding to lawful requests from authorities, and complying with applicable law.
          </LLi>
          <LLi>
            <strong>Consent (Art. 6(1)(a)):</strong> For optional marketing communications to
            prospects, non-essential cookies, and any other processing where we ask for explicit
            consent. You may withdraw consent at any time without affecting prior processing.
          </LLi>
        </LUl>
        <LP>
          For special category data (if ever collected, e.g., disability information provided for
          reasonable accommodation during an interview process), we rely on Art. 9(2)(b) (employment
          law obligations) or explicit consent (Art. 9(2)(a)).
        </LP>
      </>
    ),
  },
  {
    id: 'data-sharing',
    heading: 'Data Sharing and Disclosure',
    body: (
      <>
        <LP>
          We do not sell your personal data. We share it only in the following limited
          circumstances:
        </LP>

        <LH3>5.1 Service Providers (Sub-processors)</LH3>
        <LP>
          We engage carefully vetted third-party companies to help operate our Services, including
          cloud infrastructure, payment processing, email delivery, analytics, and customer
          support tooling. These providers are bound by data processing agreements and may only
          process data on our documented instructions. Our full sub-processor list is available in
          the Trust Center.
        </LP>

        <LH3>5.2 Customer's Authorised Users</LH3>
        <LP>
          Within a GoGrabJob Business account, personal data (including candidate profiles and
          hiring activity) is visible to users who have been granted access by the account
          administrator. Admins are responsible for managing permissions appropriately.
        </LP>

        <LH3>5.3 Business Transfers</LH3>
        <LP>
          In the event of a merger, acquisition, sale of assets, or corporate restructuring,
          personal data may be transferred to the acquiring entity. We will notify affected users
          via email and in-platform notification at least 30 days before any such transfer and
          provide an opportunity to delete accounts if desired.
        </LP>

        <LH3>5.4 Legal Requirements</LH3>
        <LP>
          We may disclose personal data when required to do so by law, court order, or regulatory
          authority; to enforce our Terms of Service; or to protect the rights, property, or
          safety of Zykes, our customers, or the public. We will notify you of such requests to
          the extent permitted by law.
        </LP>

        <LH3>5.5 With Your Consent</LH3>
        <LP>
          We may share data with third parties for any other purpose with your explicit prior
          consent.
        </LP>
      </>
    ),
  },
  {
    id: 'international-transfers',
    heading: 'International Data Transfers',
    body: (
      <>
        <LP>
          Zykes Technologies LLP is based in India. Our cloud infrastructure is hosted primarily
          on servers located in India, with regional nodes in Europe (EU) and the United States
          for performance and redundancy. If you are located in the EEA or the UK, your data may
          be transferred outside your home jurisdiction.
        </LP>
        <LP>We safeguard international transfers using:</LP>
        <LUl>
          <LLi>
            <strong>Standard Contractual Clauses (SCCs):</strong> The European Commission's
            approved SCCs (2021 version) are incorporated into our data processing agreements
            for EEA-originating data transfers.
          </LLi>
          <LLi>
            <strong>Adequacy decisions:</strong> Where the European Commission has recognised a
            country as providing adequate protection, we rely on that adequacy determination.
          </LLi>
          <LLi>
            <strong>UK International Data Transfer Agreements (IDTAs):</strong> For transfers from
            the UK, we use the ICO-approved IDTA addendum.
          </LLi>
        </LUl>
        <LP>
          You may request a copy of the applicable transfer safeguards by contacting{' '}
          <a
            href="mailto:privacy@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            privacy@gograbjob.com
          </a>
          .
        </LP>
      </>
    ),
  },
  {
    id: 'data-retention',
    heading: 'Data Retention',
    body: (
      <>
        <LP>
          We retain personal data only for as long as necessary to fulfil the purposes outlined in
          this Privacy Policy, unless a longer retention period is required by law.
        </LP>
        <LTable
          headers={['Data Category', 'Retention Period', 'Basis']}
          rows={[
            ['Account & profile data', 'Duration of subscription + 90 days post-cancellation', 'Contract'],
            ['Billing & financial records', '7 years from transaction date', 'Legal obligation (tax law)'],
            ['Candidate data (customer content)', 'Per customer configuration (default: 2 years after last activity)', 'Contract / Controller instruction'],
            ['Support tickets & communications', '3 years from ticket closure', 'Legitimate interest'],
            ['Server & security logs', '12 months rolling', 'Legitimate interest (security)'],
            ['Marketing consent records', 'Until withdrawn + 3 years', 'Legal obligation (consent evidence)'],
            ['Anonymised analytics', 'Indefinitely (no personal data)', 'Legitimate interest'],
          ]}
        />
        <LP>
          After the retention period expires, data is securely deleted or irreversibly
          anonymised. For customer-controlled candidate data, customers may configure custom
          retention periods and initiate deletion through the platform at any time.
        </LP>
      </>
    ),
  },
  {
    id: 'your-rights',
    heading: 'Your Privacy Rights',
    body: (
      <>
        <LP>
          Depending on your location and applicable law, you may have the following rights with
          respect to your personal data:
        </LP>
        <LUl>
          <LLi>
            <strong>Right of access (Art. 15 GDPR):</strong> Request a copy of the personal data
            we hold about you, including information about how it is processed.
          </LLi>
          <LLi>
            <strong>Right to rectification (Art. 16 GDPR):</strong> Request correction of
            inaccurate or incomplete personal data.
          </LLi>
          <LLi>
            <strong>Right to erasure (Art. 17 GDPR):</strong> Request deletion of your personal
            data where there is no compelling reason for its continued processing ("right to be
            forgotten").
          </LLi>
          <LLi>
            <strong>Right to restriction of processing (Art. 18 GDPR):</strong> Request that we
            limit how we use your data in certain circumstances.
          </LLi>
          <LLi>
            <strong>Right to data portability (Art. 20 GDPR):</strong> Receive your personal data
            in a structured, commonly used, machine-readable format.
          </LLi>
          <LLi>
            <strong>Right to object (Art. 21 GDPR):</strong> Object to processing based on
            legitimate interests, including direct marketing.
          </LLi>
          <LLi>
            <strong>Rights related to automated decision-making (Art. 22 GDPR):</strong> Not be
            subject to decisions based solely on automated processing that significantly affect
            you, without human review.
          </LLi>
          <LLi>
            <strong>CCPA rights (California residents):</strong> Right to know, right to delete,
            right to opt-out of sale, and right to non-discrimination.
          </LLi>
          <LLi>
            <strong>DPDPA rights (India residents):</strong> Right of access, correction, erasure,
            and grievance redressal under the Digital Personal Data Protection Act, 2023.
          </LLi>
        </LUl>
        <LP>
          To exercise any of these rights, please submit a request to{' '}
          <a
            href="mailto:privacy@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            privacy@gograbjob.com
          </a>{' '}
          or use the Data Subject Request form in your account settings. We will respond within
          30 days (or 45 days where permitted by applicable law with notice). We may need to verify
          your identity before fulfilling the request.
        </LP>
        <LNote>
          If you are a job applicant whose data was submitted by an employer using GoGrabJob
          Business, please direct your data rights request to that employer (the data controller).
          We will assist the controller in responding to requests within the timeframes defined in
          our Data Processing Agreement.
        </LNote>
      </>
    ),
  },
  {
    id: 'security',
    heading: 'Security',
    body: (
      <>
        <LP>
          We implement industry-standard technical and organisational measures to protect your
          personal data against unauthorised access, alteration, disclosure, or destruction.
          Our security programme includes:
        </LP>
        <LUl>
          <LLi>
            <strong>Encryption at rest:</strong> AES-256 encryption for all customer data stored
            on our infrastructure.
          </LLi>
          <LLi>
            <strong>Encryption in transit:</strong> TLS 1.3 enforced on all endpoints, with HTTP
            Strict Transport Security (HSTS) headers.
          </LLi>
          <LLi>
            <strong>Access control:</strong> Role-based access control (RBAC) with least-privilege
            principles; multi-factor authentication available and enforced for admin accounts.
          </LLi>
          <LLi>
            <strong>Vulnerability management:</strong> Annual penetration testing by an
            independent CREST-certified firm; continuous automated scanning for known
            vulnerabilities.
          </LLi>
          <LLi>
            <strong>Audit logging:</strong> All administrative and user actions are logged with
            tamper-resistant timestamps.
          </LLi>
          <LLi>
            <strong>Incident response:</strong> Documented incident response plan; EEA data breach
            notifications to supervisory authorities within 72 hours where required by GDPR.
          </LLi>
        </LUl>
        <LP>
          However, no system is completely secure. We cannot guarantee absolute security of your
          data. If you suspect a security issue, please report it to{' '}
          <a
            href="mailto:security@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            security@gograbjob.com
          </a>
          .
        </LP>
      </>
    ),
  },
  {
    id: 'cookies',
    heading: 'Cookies & Tracking Technologies',
    body: (
      <>
        <LP>
          We use cookies and similar technologies (pixels, local storage) for session management,
          security, analytics, and — where you have consented — marketing purposes. For full
          details of the cookies we use, the categories they fall into, and how to manage your
          preferences, please refer to our{' '}
          <a
            href="/business/cookie-policy"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            Cookie Policy
          </a>
          .
        </LP>
        <LP>
          You can update your cookie preferences at any time using the Cookie Settings panel
          accessible from the footer of any GoGrabJob Business marketing page.
        </LP>
      </>
    ),
  },
  {
    id: 'childrens-privacy',
    heading: "Children's Privacy",
    body: (
      <>
        <LP>
          The GoGrabJob Business platform is designed for use by business professionals and is not
          directed at children under the age of 16 (or 13 in jurisdictions where the lower
          threshold applies). We do not knowingly collect personal data from children.
        </LP>
        <LP>
          If you believe that a child has provided us with personal data without appropriate
          consent, please contact us at{' '}
          <a
            href="mailto:privacy@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            privacy@gograbjob.com
          </a>{' '}
          and we will promptly delete such information.
        </LP>
      </>
    ),
  },
  {
    id: 'third-party-links',
    heading: 'Third-Party Links & Integrations',
    body: (
      <>
        <LP>
          Our Services may contain links to third-party websites or integrate with third-party
          services (e.g., LinkedIn, job boards, HRIS systems, calendar providers). This Privacy
          Policy does not apply to those third-party services. We encourage you to review the
          privacy policies of any third party you interact with through our platform.
        </LP>
        <LP>
          When you enable an integration, we will clearly describe what data will be exchanged and
          request your authorisation before any such exchange takes place.
        </LP>
      </>
    ),
  },
  {
    id: 'policy-changes',
    heading: 'Changes to This Privacy Policy',
    body: (
      <>
        <LP>
          We may update this Privacy Policy from time to time to reflect changes in our practices,
          technology, legal requirements, or other factors. When we make material changes, we will:
        </LP>
        <LOl>
          <LLi>Update the "Last updated" date at the top of this page.</LLi>
          <LLi>
            Send an email notification to the primary contact on each customer account at least 30
            days before the new policy takes effect (for material changes).
          </LLi>
          <LLi>Display an in-platform banner for active users.</LLi>
        </LOl>
        <LP>
          Continued use of the Services after the effective date of an updated Privacy Policy
          constitutes your acceptance of the changes. If you object to any changes, you may
          terminate your account in accordance with our Terms of Service.
        </LP>
        <LP>
          Previous versions of this Privacy Policy are available on request from{' '}
          <a
            href="mailto:privacy@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            privacy@gograbjob.com
          </a>
          .
        </LP>
      </>
    ),
  },
  {
    id: 'contact',
    heading: 'Contact & Complaints',
    body: (
      <>
        <LP>
          For any questions, concerns, or data subject requests relating to this Privacy Policy,
          please contact:
        </LP>
        <LP>
          <strong>Data Protection Contact</strong>
          <br />
          Zykes Technologies LLP
          <br />
          Email:{' '}
          <a
            href="mailto:privacy@gograbjob.com"
            className="text-violet-600 hover:underline dark:text-violet-400"
          >
            privacy@gograbjob.com
          </a>
          <br />
          Response time: within 30 days
        </LP>
        <LP>
          <strong>Complaints — EU/EEA residents:</strong> If you are located in the EEA and
          believe we have not adequately addressed your concern, you have the right to lodge a
          complaint with your national data protection supervisory authority (e.g., the Irish
          Data Protection Commission, the CNIL in France, or the ICO in the UK).
        </LP>
        <LP>
          <strong>Complaints — India residents:</strong> You may raise a grievance with our
          Grievance Officer at the contact above, or approach the Data Protection Board of India
          once established under the DPDPA 2023.
        </LP>
        <LNote>
          We are committed to resolving privacy concerns fairly and promptly. Please give us the
          opportunity to address your concern before escalating to a supervisory authority.
        </LNote>
      </>
    ),
  },
];

export function PrivacyPolicyPage() {
  return (
    <LegalDocLayout
      category="Legal"
      title="Privacy Policy"
      subtitle="How Zykes Technologies LLP collects, uses, and protects your personal information across GoGrabJob Business."
      effectiveDate="1 April 2026"
      lastUpdated="14 April 2026"
      version="2.1"
      sections={sections}
      contactEmail="privacy@gograbjob.com"
    />
  );
}
